import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Video, Square, Loader2, CheckCircle2, UploadCloud, Camera } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Public phone-recording page reached via the Video Studio QR / secure link.
 *  Primary path uses the phone's NATIVE camera app (reliable on iOS + Android);
 *  an in-browser recorder is offered as a fallback. */
export const MobileVideoCapturePage = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [recording, setRecording] = useState(false);
  const [blob, setBlob] = useState(null);   // File (native capture) or Blob (browser record)
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);

  const videoRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/video/mobile/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'This link is invalid or expired.'));
  }, [token]);

  // Manage the preview object URL lifecycle.
  useEffect(() => {
    if (!blob) { setPreviewUrl(null); return undefined; }
    const url = URL.createObjectURL(blob);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  // Native camera — opens the phone's own camera/recorder app.
  const onNativeCapture = (e) => {
    const file = e.target.files?.[0];
    if (file) setBlob(file);
    e.target.value = '';
  };

  // In-browser recorder (fallback).
  const start = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment', width: 720 }, audio: true });
      streamRef.current = s;
      if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.muted = true; await videoRef.current.play(); }
      chunksRef.current = [];
      const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus') ? 'video/webm;codecs=vp9,opus' : 'video/webm';
      const rec = new MediaRecorder(s, { mimeType: mime });
      rec.ondataavailable = (ev) => { if (ev.data.size) chunksRef.current.push(ev.data); };
      rec.onstop = () => {
        if (videoRef.current) videoRef.current.srcObject = null;
        setBlob(new Blob(chunksRef.current, { type: 'video/webm' }));
        s.getTracks().forEach((t) => t.stop());
      };
      recRef.current = rec; rec.start(); setRecording(true);
    } catch {
      setError('In-browser recording isn’t supported on this device — use “Open camera” above instead.');
    }
  };

  const stop = () => { if (recRef.current?.state !== 'inactive') recRef.current?.stop(); setRecording(false); };

  const upload = async () => {
    if (!blob) return;
    setUploading(true);
    try {
      const name = (blob instanceof File && blob.name) ? blob.name : 'mobile.webm';
      const file = blob instanceof File ? blob : new File([blob], name, { type: blob.type || 'video/webm' });
      const fd = new FormData();
      fd.append('file', file, name);
      await axios.post(`${API}/video/mobile/${token}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDone(true);
    } catch {
      setError('Upload failed — please try again.');
    } finally { setUploading(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white flex flex-col items-center justify-center p-5" data-testid="mobile-capture-page">
      {error ? (
        <p className="text-rose-400 text-center text-sm max-w-xs" data-testid="mobile-error">{error}</p>
      ) : done ? (
        <div className="text-center space-y-3" data-testid="mobile-done">
          <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
          <p className="font-semibold">Sent to your editor!</p>
          <p className="text-sm text-zinc-400">Return to PostToSocial on your computer and tap "load my clip".</p>
        </div>
      ) : !info ? (
        <Loader2 className="w-7 h-7 animate-spin text-indigo-400" />
      ) : (
        <div className="w-full max-w-sm space-y-4 text-center">
          <p className="text-sm text-zinc-300">Recording for <strong>{info.project_name}</strong></p>

          <video
            ref={videoRef}
            playsInline
            className="w-full rounded-xl bg-black aspect-[9/16] object-cover"
            src={previewUrl || undefined}
            controls={!!previewUrl}
          />

          {!blob ? (
            <div className="space-y-3">
              {/* Primary: native phone camera */}
              <div className="relative block">
                <span className="flex items-center justify-center w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-md py-2.5">
                  <Camera className="w-4 h-4 mr-2" /> Open camera to record
                </span>
                <input
                  type="file"
                  accept="video/*"
                  capture="environment"
                  onChange={onNativeCapture}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  aria-label="Open camera to record"
                  data-testid="mobile-native-capture"
                />
              </div>
              <p className="text-[11px] text-zinc-500">Records using your phone’s camera, then sends it back to your editor.</p>

              {/* Fallback: in-browser recorder */}
              {recording ? (
                <Button onClick={stop} variant="destructive" className="w-full" data-testid="mobile-stop"><Square className="w-4 h-4 mr-2" /> Stop recording</Button>
              ) : (
                <Button onClick={start} variant="outline" className="w-full text-zinc-900" data-testid="mobile-record"><Video className="w-4 h-4 mr-2" /> Record in browser instead</Button>
              )}
            </div>
          ) : (
            <div className="flex gap-2">
              <Button onClick={() => setBlob(null)} variant="outline" className="flex-1 text-zinc-900" data-testid="mobile-retake">Retake</Button>
              <Button onClick={upload} disabled={uploading} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="mobile-upload">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UploadCloud className="w-4 h-4 mr-2" />} Send
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MobileVideoCapturePage;
